/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react'
import { GenericSnippet, SnippetIdentifier, SnippetsConfig } from '../contexts/SettingsContext.d'

export const snippetIdentifier = (snippet: GenericSnippet): SnippetIdentifier =>
    'key' in snippet
        ? 'attributes'
        : 'str' in snippet
            ? 'snippets'
            : 'aliases'

export const snippetKey = (snippet: GenericSnippet): string =>
    snippet.snippetKey
        ? snippet.snippetKey
        : 'key' in snippet
            ? snippet.key
            : 'str' in snippet
                ? snippet.str
                : snippet.canonical

export const snippetModifyKey = (snippet: GenericSnippet, savedValue: string): string =>
    `${snippet.type}-${snippetKey(snippet)}-${savedValue}`

const snippetsConfig: SnippetsConfig = {
    aliases: {
        title: 'Aliases',
        icon: 'tags',
        new: 'Add canonical text',
        description: <>
            <p>Here, you can indicate different tags (or similar) which refer to the same person or concept.</p>
            <p>
                Each<span className="alias-alias">alias</span> will be replaced by
                the corresponding <span className="alias-canonical">canonical name/tag</span>
                within all Trove&apos;s analysis.
            </p>
            <p>
                Power user tip:<span className="alias-alias">aliases</span> with the following characters support <a
                    href="https://www.regular-expressions.info/"
                    rel="noreferrer"
                    target="_blank">
                    regular expressions
                </a>:
                <span className="code"> *+?[]\b\B</span>
            </p>
        </>,
        types: {
            names: {
                title: 'Names and tags',
                icon: 'signature',
                placeholder: 'John (Smith)',
                description: <>
                    <p>
                        Any combination of tags or names can be used.
                        For names, only the fully expanded version should be referenced,
                        e.g. “JohnIE (Smith)” rather than “John,” “JohnIE,” or “John Smith.”
                    </p>
                    <p>
                        Note that <em>all</em> text blocks included here will be tracked by Trove, even if they are not a name or a tag.
                        This applies even for <span className="alias-canonical">canonical names/tags</span>
                        with no<span className="alias-alias">aliases</span>.
                    </p>
                </>
            },
            firstnames: {
                title: 'First names',
                icon: 'signature',
                placeholder: 'Elizabeth',
                description: <>
                    Any first names matching a<span className="alias-alias">nickname</span>{' '}
                    like<span className="alias-alias">Liz</span> will be switched out for the{' '}
                    <span className="alias-canonical">canonical</span>name like{' '}
                    <span className="alias-canonical">Elizabeth</span>when you’ve mentioned both “Liz (Jones)” and “Elizabeth (Jones)”.
                </>
            },
            countries: {
                title: 'Countries',
                icon: 'flag',
                placeholder: 'IE',
                description: <>
                    Names linked to any<span className="alias-alias">alias country</span> will instead reference
                    the <span className="alias-canonical">canonical country</span>
                    - and <em>all</em> listed <span className="alias-canonical">countries</span> and
                    <span className="alias-alias">aliases</span> will be recognised as countries even if no full names reference them.
                </>
            },
            titles: {
                title: 'Titles',
                icon: 'person-rays',
                placeholder: 'Doctor',
                description: <>
                    Names with<span className="alias-alias">alias titles</span> will be presented as having
                    the <span className="alias-canonical">canonical title</span> – for example, merging
                    titles across different languages.
                </>
            }
        }
    },
    snippets: {
        title: 'Snippets',
        new: 'Add text',
        types: {
            'false.positives': {
                title: 'False-positive “first names”',
                icon: 'person-circle-question',
                placeholder: 'She',
                description: <>
                    These will simply be skipped when detected as a first name: e.g. “She (Jane) went...”
                    is referring to Jane, not someone called She Jane.
                </>
            },
            'red.herrings': {
                title: '“Red herrings”',
                icon: 'text-slash',
                placeholder: 'John Smith Street',
                description: <>
                    These will be entirely ignored from all processing: e.g. references to the road “John Smith Street”
                    should not risk matching “John (Smith).”
                </>
            }
        }
    },
    attributes: {
        title: 'Attributes',
        new: 'Add country',
        types: {
            'country.colours': {
                title: 'Bespoke country colours',
                icon: 'flag',
                placeholder: 'FR',
                description: <>
                    In wordclouds, names linked to these countries will be displayed in a specific colour,
                    specified in <a
                        target="_blank"
                        rel="noreferrer"
                        href="https://www.w3schools.com/colors/colors_mixer.asp?colorbottom=0000FF&colortop=FFFFFF">
                        HTML notation
                    </a>.
                </>
            }
        }
    }
}

export default snippetsConfig
